import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
const TRUST_IMG_WIDE = 'https://cdn.shopify.com/s/files/1/0995/0671/6970/files/6Testimonial_Trust_Section_Ratio16_9.jpg?v=1777847679';
const TRUST_IMG_SQUARE = 'https://cdn.shopify.com/s/files/1/0995/0671/6970/files/6Testimonial_Trust_Section_Ratio1_1.jpg?v=1777847679';
const testimonials = [{
  quote: 'I finally know my actual profit margin — not just what Shopify tells me. This dashboard changed how I run my business completely.',
  name: 'Sarah M.',
  role: 'Etsy Seller — Texas',
  initials: 'SM',
  avatarBg: 'bg-gradient-teal'
}, {
  quote: 'Set it up during my lunch break. Now I check my numbers every morning with coffee. It\'s become my favorite business ritual.',
  name: 'David K.',
  role: 'Amazon FBA — California',
  initials: 'DK',
  avatarBg: 'bg-[#FFB800]'
}, {
  quote: 'Worth 10x the price. I was paying an accountant $200/month for less insight than this spreadsheet gives me for free — forever.',
  name: 'Maria L.',
  role: 'Shopify Owner — Florida',
  initials: 'ML',
  avatarBg: 'bg-gradient-purple'
}];
const featuredStats = [{
  value: '+61%',
  label: 'Profit Margin Jump'
}, {
  value: '2 hrs',
  label: 'Saved Per Week'
}, {
  value: '$0',
  label: 'Monthly Cost'
}];
function Stars({
  size = 4
}: {
  size?: number;
}) {
  return <div className="flex gap-0.5" data-id="element-272">
      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-${size} h-${size} fill-[#FFB800] text-[#FFB800]`} data-id="element-273" />)}
    </div>;
}
export function TestimonialsSection() {
  return <section id="testimonials" className="bg-[#060B18] py-24 relative overflow-hidden" data-id="element-274">
      <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-purple/5 rounded-full blur-[120px] pointer-events-none" data-id="element-275" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-276">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-14" data-id="element-277">
          <div className="flex justify-center mb-4" data-id="element-278">
            <div className="flex gap-1" data-id="element-279">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-6 h-6 fill-[#FFB800] text-[#FFB800]" data-id="element-280" />)}
            </div>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white mb-3" data-id="element-281">
            Small Business Owners{' '}
            <span className="text-gradient-teal" data-id="element-282">Love It</span>
          </h2>
          <p className="text-white/40 text-sm" data-id="element-283">Real people. Real results.</p>
        </motion.div>

        {/* Trust image — 16:9, full width above cards */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative rounded-2xl overflow-hidden mb-10 group" style={{
        aspectRatio: '16/9',
        maxHeight: '420px'
      }} data-id="element-284">
          <img src={TRUST_IMG_WIDE} alt="Small business owners using the Command Center dashboard" className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-285" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060B18] via-[#060B18]/30 to-transparent" data-id="element-286" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060B18]/60 via-transparent to-[#060B18]/40" data-id="element-287" />

          {/* Overlay text on image */}
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between" data-id="element-288">
            <div data-id="element-289">
              <p className="font-display font-extrabold text-white text-2xl sm:text-3xl mb-1" data-id="element-290">
                Join Thousands of Founders
              </p>
              <p className="text-white/60 text-sm" data-id="element-291">who finally know their real numbers</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-5 py-2.5" data-id="element-292">
              <Stars size={3} data-id="element-293" />
              <span className="text-white text-sm font-semibold ml-1" data-id="element-294">4.9 avg rating</span>
            </div>
          </div>
        </motion.div>

        {/* 3 testimonial cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-295">
          {testimonials.map((t, i) => <motion.div key={t.name} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.12,
          duration: 0.5
        }} whileHover={{
          y: -5
        }} className="glass-card rounded-2xl p-6 hover:border-white/15 transition-all duration-300" style={{
          boxShadow: '0 0 30px rgba(0,0,0,0.2)'
        }} data-id="element-296">
              <Stars size={4} data-id="element-297" />
              <p className="text-white/70 text-sm italic leading-relaxed my-4" data-id="element-298">"{t.quote}"</p>
              <div className="flex items-center gap-3 pt-3 border-t border-white/5" data-id="element-299">
                <div className={`w-10 h-10 ${t.avatarBg} rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0`} data-id="element-300">
                  {t.initials}
                </div>
                <div data-id="element-301">
                  <p className="text-white text-sm font-semibold" data-id="element-302">{t.name}</p>
                  <p className="text-white/40 text-xs" data-id="element-303">{t.role}</p>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Featured testimonial */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="glass-card rounded-2xl p-8 sm:p-10" style={{
        border: '1px solid rgba(0,201,177,0.12)',
        boxShadow: '0 0 60px rgba(0,201,177,0.06)'
      }} data-id="element-304">
          <div className="grid md:grid-cols-3 gap-8 items-center" data-id="element-305">
            <div className="md:col-span-2" data-id="element-306">
              <Stars size={5} data-id="element-307" />
              <p className="text-white/80 text-lg italic leading-relaxed mt-4 mb-6" data-id="element-308">
                "Before this dashboard, I was flying blind. I thought my business was profitable because revenue was growing — but I was actually losing money on 3 of my top 5 products. Within a week, I cut the losers, doubled down on winners, and saw my real profit jump 61%. This isn't just a spreadsheet — it's a business partner."
              </p>
              <div className="flex items-center gap-3" data-id="element-309">
                <div className="w-12 h-12 bg-gradient-teal rounded-full flex items-center justify-center text-white font-bold text-sm" data-id="element-310">
                  JW
                </div>
                <div data-id="element-311">
                  <p className="text-white font-semibold" data-id="element-312">James W.</p>
                  <p className="text-white/40 text-sm" data-id="element-313">Shopify Store Owner — New York</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-1 gap-3" data-id="element-314">
              {featuredStats.map(stat => <div key={stat.label} className="bg-white/5 border border-white/5 rounded-xl p-4 text-center" data-id="element-315">
                  <p className="text-2xl font-display font-extrabold text-gradient-teal" data-id="element-316">{stat.value}</p>
                  <p className="text-white/40 text-xs mt-1" data-id="element-317">{stat.label}</p>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}